import { useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import TrialBanner from '../TrialBanner';
import SubscriptionReminderModal from '../SubscriptionReminderModal';
import InstallHint from '../common/InstallHint';
import MobileBottomBar from '../common/MobileBottomBar';
import useImpersonation from '../../hooks/useImpersonation';
import useAuth from '../../hooks/useAuth';
import { FaEye, FaLock } from 'react-icons/fa';
import { MdWarningAmber } from 'react-icons/md';

// Wraps every admin page: provides the persistent Navbar + Sidebar shell.
// Individual admin pages just render their content — no need to include the shell.
function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isImpersonating, isReadOnly, isFullAccess, clubName } = useImpersonation();
  const { user } = useAuth();
  const { pathname } = useLocation();

  // Suspended-club recovery: restrict the admin to the subscription page only. Not the real
  // security boundary (the backend enforces this regardless of what renders here) — just UX, so
  // the admin isn't shown a full nav that silently bounces on every click. Impersonation already
  // bypasses the equivalent backend check, so it bypasses this too — a support session must still
  // be able to see the whole club.
  const isSuspended = !isImpersonating && user?.clubStatus === 'suspended';
  if (isSuspended && pathname !== '/admin/subscription') {
    return <Navigate to="/admin/subscription" replace />;
  }

  // Restricted shell — no Sidebar (nothing else to navigate to), just the banner + the
  // subscription page itself. Navbar stays (the admin still needs sign-out/theme access).
  if (isSuspended) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar onMenuToggle={() => setSidebarOpen(prev => !prev)} />
        <main className="flex-1 overflow-auto min-w-0 flex flex-col">
          <div className="w-full px-4 py-3 flex items-center gap-2 text-sm font-semibold bg-amber-50 text-amber-700 border-b border-amber-200">
            <MdWarningAmber className="flex-shrink-0 text-lg" />
            <span>Your subscription is overdue. Submit a payment to restore access.</span>
          </div>
          <div className="flex-1 p-4 sm:p-6 pb-20 md:pb-6">
            <Outlet />
          </div>
        </main>
        <InstallHint />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background ${isImpersonating ? 'pt-10' : ''}`}>
      <Navbar onMenuToggle={() => setSidebarOpen(prev => !prev)} />
      <div className="flex">
        {/* Mobile backdrop — clicking it closes the sidebar */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <main className="flex-1 overflow-auto min-w-0 flex flex-col">
          <TrialBanner />

          {/* Read-only impersonation bar — dark amber tint, high-contrast light text (theme-robust) */}
          {isImpersonating && isReadOnly && (
            <div className="w-full px-4 py-2 flex items-center gap-2 text-xs font-semibold"
              style={{ background: 'rgba(146,64,14,0.92)', borderBottom: '1px solid rgba(252,211,77,0.4)', color: '#fde68a' }}>
              <FaEye className="flex-shrink-0" style={{ color: '#fcd34d' }} />
              <span>
                <strong style={{ color: '#fef3c7' }}>Read-only support mode</strong> — write actions are disabled.
                You are viewing <strong style={{ color: '#fef3c7' }}>{clubName}</strong> as a read-only observer.
                Exit and restart in Full Access mode if changes are required.
              </span>
            </div>
          )}

          {/* Full-access impersonation bar — dark red/danger tint, high-contrast light text */}
          {isImpersonating && isFullAccess && (
            <div className="w-full px-4 py-2 flex items-center gap-2 text-xs font-semibold"
              style={{ background: 'rgba(127,29,29,0.92)', borderBottom: '1px solid rgba(248,113,113,0.45)', color: '#fecaca' }}>
              <FaLock className="flex-shrink-0" style={{ color: '#fca5a5' }} />
              <span>
                <strong style={{ color: '#fee2e2' }}>Full-access support mode</strong> — changes you make inside <strong style={{ color: '#fee2e2' }}>{clubName}</strong> are real and logged permanently.
              </span>
            </div>
          )}

          <div className="flex-1 p-4 sm:p-6 pb-20 md:pb-6">
            <Outlet />
          </div>
        </main>
      </div>
      <MobileBottomBar onMenuToggle={() => setSidebarOpen(prev => !prev)} />
      <InstallHint />
      {/* Subscription due/overdue reminder — shows on dashboard/refresh for the club admin */}
      {!isImpersonating && <SubscriptionReminderModal />}
    </div>
  );
}

export default AdminLayout;
